/* eslint-disable no-unused-vars */
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorForBtn, Label, RadioBox, Separator, Buttons, Each } from 'components';
import { RadioBoxGroup, RightSideContainer } from '../components/';
import { SelectedItem } from './selectedItem/SelectedItem';
import SectionLayout from 'components/common/SectionLayout';
import ChooseLocationBtn from './chooseLocationBtn/ChooseLocationBtn';
import { generalActions } from 'store/campaign/general';
import {
  selectTargettingErrorFlag,
  select_tier_locations,
  selectedLocationsList,
} from 'store/selectors';
import {
  targetingPlacementsActions,
  formErrorsCampaign_action,
  creativesActions,
} from 'store/actions';
import { createLocationsStrToSave, goToLocationParent, getMinTierValue } from 'utils';
import * as S from './TargetingLocations.styled';

const checkNameLocationLength = (itemData) => {
  if (itemData.type === 'continent') {
    const crateStrName = `Continent: ${itemData.continent_name}`;
    return crateStrName.length;
  }
  if (itemData.type === 'country') {
    const crateStrName = `Country:${itemData.country_name} - ${itemData.country_code}`;
    return crateStrName.length;
  }
  if (itemData.type === 'region') {
    const crateStrName = ` Region: ${itemData.region_name} / ${itemData.country_name} -
    ${itemData.country_code}`;
    return crateStrName.length;
  }
  if (itemData.type === 'city') {
    const crateStrName = `${itemData.country_name} - ${itemData.country_code} / ${itemData.region_name} / City: ${itemData.city}`;
    return crateStrName.length;
  }
};

const getItemName = (itemData) => {
  const itemType = itemData.type;
  const fieldWithName = itemType !== 'city' ? `${itemType}_name` : itemType;
  const itemName = itemData[fieldWithName].toLowerCase();
  return itemName;
};

export default function TargetingOngoingLocations() {
  const dispatch = useDispatch();
  const errorFlag = useSelector(selectTargettingErrorFlag);
  const tireCountriesMap = useSelector(select_tier_locations);
  const selected_locations = useSelector(selectedLocationsList);

  const includedItems = selected_locations?.filter((e) => e?.status === 'include');
  const excludedItems = selected_locations?.filter((e) => e?.status === 'exclude');

  const locationToggleState = useSelector((state) =>
    parseInt(state.general.mainCampaignObject?.currentCampaignObj?.location),
  );

  useEffect(() => {
    // don't clear for 'all countries'
    if (selected_locations?.length && includedItems.every((item) => item.country_code !== 'RU')) {
      clearCretiveTokens();
    }
  }, [selected_locations]);

  function clearCretiveTokens() {
    dispatch(creativesActions.clearCreativeTokenIDs());
  }

  const handleLocationChange = (value) => {
    if (selected_locations.length) {
      clearAllIncludedItems();
    }
    if (errorFlag.locations) {
      dispatch(
        targetingPlacementsActions.updateErrorField({
          errorField: 'locations',
          newValue: false,
        }),
      );
    }
    dispatch(
      formErrorsCampaign_action.updateErrorField({ errorField: 'targeting', newValue: false }),
    );
    dispatch(
      generalActions.updateCurrentCampaignObj({
        propField: 'location',
        newValue: value,
      }),
    );
  };

  const clearAllExcludedItems = useCallback(() => {
    const newLocations = selected_locations.filter((e) => e?.status === 'include');
    dispatch(generalActions.updateSelectedLocations(newLocations));
    const dataForSavingInDataBase = `${newLocations.map((item) => item.value).join(',')}|`;
    dispatch(generalActions.updateSelectedLocationsForSaving(dataForSavingInDataBase));
    dispatch(generalActions.setTierField(0));
  }, [selected_locations]);

  const clearAllIncludedItems = useCallback(() => {
    dispatch(generalActions.updateSelectedLocations([]));
    dispatch(generalActions.updateSelectedLocationsForSaving(`|`));
    dispatch(generalActions.setTierField(0));
  }, []);

  const handleDeleteItem = useCallback(
    (itemData) => {
      if (itemData.status === 'exclude') {
        const newExcludedItems = excludedItems.filter(({ value }) => value !== itemData.value);
        dispatch(generalActions.updateSelectedLocations([...newExcludedItems, ...includedItems]));
        dispatch(
          generalActions.updateSelectedLocationsForSaving(
            createLocationsStrToSave(includedItems, newExcludedItems),
          ),
        );
      }
      if (itemData.status === 'include') {
        if (itemData.country_code === 'RU') clearCretiveTokens();

        const newIncludedItems = includedItems.filter(({ value }) => value !== itemData.value);
        const newExcludedItems = excludedItems.filter(
          ({ value }) => goToLocationParent(value, itemData.type) !== itemData.value,
        );
        const minTierValue = getMinTierValue(newIncludedItems, tireCountriesMap);
        dispatch(generalActions.setTierField(minTierValue));
        dispatch(
          generalActions.updateSelectedLocations([...newIncludedItems, ...newExcludedItems]),
        );
        dispatch(
          generalActions.updateSelectedLocationsForSaving(
            createLocationsStrToSave(newIncludedItems, newExcludedItems),
          ),
        );
      }
    },
    [excludedItems, includedItems],
  );

  return (
    <>
      <SectionLayout
        noSeparator={true}
        noBottomPadding={true}
        leftSide={<Label text='Location:' variant='label' style={{ marginTop: '8px' }} />}
        rightSide={
          <RightSideContainer>
            <RadioBoxGroup>
              <RadioBox.NormalSize
                onChange={handleLocationChange}
                label='All Countries'
                width={130}
                variant='left'
                value={1}
                pressed={locationToggleState === 1}
              />
              <RadioBox.NormalSize
                onChange={handleLocationChange}
                label='Specific Countries'
                width={160}
                variant='right'
                value={2}
                pressed={locationToggleState === 2}
              />
            </RadioBoxGroup>
            {locationToggleState === 2 && (
              <S.MultipleItemsSelectionComponent showBorder={errorFlag.locations}>
                <ChooseLocationBtn />
                {includedItems.length ? (
                  <>
                    <Separator />
                    <S.SelectedItems>
                      <S.ClearBtnSection>
                        <label>Include:</label>
                        <Buttons.TertiarySmall
                          name='inc_location_clear_all'
                          id='inc_location_clear_all'
                          onClick={clearAllIncludedItems}
                        >
                          Clear All
                        </Buttons.TertiarySmall>
                      </S.ClearBtnSection>
                      <S.Box>
                        {
                          <Each
                            fieldOfIndex='value'
                            of={includedItems.sort((a, b) => getItemName(a) - getItemName(b))}
                            render={(item, index) => {
                              return (
                                <SelectedItem
                                  env='include'
                                  itemData={item}
                                  handleDeleteItem={handleDeleteItem}
                                />
                              );
                            }}
                          />
                        }
                      </S.Box>
                    </S.SelectedItems>
                  </>
                ) : null}
                {excludedItems.length ? (
                  <>
                    <Separator />
                    <S.SelectedItems>
                      <S.ClearBtnSection>
                        <label>Exclude:</label>
                        <Buttons.TertiarySmall
                          name='exc_location_clear_all'
                          id='exc_location_clear_all'
                          onClick={clearAllExcludedItems}
                        >
                          Clear All
                        </Buttons.TertiarySmall>
                      </S.ClearBtnSection>
                      <S.Box>
                        {
                          <Each
                            fieldOfIndex='value'
                            of={excludedItems.sort((a, b) => getItemName(a) - getItemName(b))}
                            render={(item, index) => {
                              return (
                                <SelectedItem
                                  env='exclude'
                                  itemData={item}
                                  handleDeleteItem={handleDeleteItem}
                                />
                              );
                            }}
                          />
                        }
                      </S.Box>
                    </S.SelectedItems>
                  </>
                ) : null}
              </S.MultipleItemsSelectionComponent>
            )}
            <ErrorForBtn errorMsg='Please choose Location' isOpen={errorFlag.locations} />
          </RightSideContainer>
        }
      />
      <Separator />
    </>
  );
}
